<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 v-if="organizationUnitId" :tid="organizationUnitId"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">
                        {{ isServiceUser ? "Manage service account's workspaces" : "Manage user's workspaces" }}
                    </h1>

                    <a :href="`/#/admin/organizations/users/manage/${userId}`" class="btn btn-link btn-sm">
                        <i class="fa fa-arrow-left" aria-hidden="true"></i> Back
                    </a>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div class="container">
            <!-- Informational alert for service accounts -->
            <div v-if="isServiceUser" class="alert alert-info" role="alert">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                <strong>Service Account Workspaces:</strong> Workspace assignments control which workspaces this service account can access through the API. Service accounts use API keys for authentication instead of passwords.
            </div>

            <h4 v-if="organizationWorkspacesLoaded && organizationWorkspacesLength==0">No workspaces available</h4>

            <div v-if="userNotInWorkspaces.length" style="margin-bottom: 40px;">
                <h4 class="fg-color-blue">
                    {{ isServiceUser ? 'Grant access to workspaces' : 'Invite user to workspaces' }}
                </h4>
                <br />
                <div class="row">
                    <div v-for="w in userNotInWorkspaces" class="col-sm-6" style="margin-bottom: 20px;">
                        <div class="card bg-light">
                            <div class="card-header">
                                {{ isServiceUser ? 'Workspace access' : 'Workspace invitation' }}
                                <button @click="handleAddUserToWorkspaceCommandV1(w)" class="btn btn-primary btn-sm pull-right">
                                    {{ isServiceUser ? 'Grant' : 'Send' }} <i :class="[isServiceUser ? 'fa fa-check' : 'fa fa-send-o']" aria-hidden="true"></i>
                                </button>
                            </div>
                            <div class="card-body">
                                <h5 class="card-title">{{w.Name}}</h5>
                                <p class="card-text"><em>{{w.Decription}}</em></p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="userInWorkspaces.length">
                <h4 class="fg-color-blue">
                    {{ isServiceUser ? "Service account's workspaces" : "User's workspaces" }}
                </h4>
                <br />

                <table class="table table-striped table-hover">
                    <tbody>
                        <tr v-for="w in userInWorkspaces">
                            <th>{{w.Name}}</th>
                            <th>{{w.Decription}}</th>
                            <td>
                                <button @click="handleRemoveUserFromWorkspaceCommandV1(w)" class="btn btn-secondary btn-sm pull-right">
                                    {{ isServiceUser ? 'Revoke access' : 'Remove an access' }}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, watch } from 'vue'
    import { useRoute } from 'vue-router'

    import AdminNavigationV2 from '@common/AdminNavigationV2.vue'

    import
        {
            useCurrentUserQueryV1,
            useUserQueryV1
        } from '@Organizations/Users/queries-vue-composables'

    import
        {
            useWorkspacesByUserQueryV1,
            useWorkspacesByOrganizationQueryV1
        } from '@Organizations/Workspaces/queries-vue-composables'

    import
        {
            useAddUserToWorkspaceCommandV1,
            useRemoveUserFromWorkspaceCommandV1
        } from '@Organizations/Workspaces/commands-vue-composables'

    // ---------------- route ----------------

    const route = useRoute()
    const userId = computed(() => String(route.params.id))

    const { runCurrentUserQueryV1 } = useCurrentUserQueryV1(false, true, false)

    const { runUserQueryV1, modelUserQueryV1: user } = useUserQueryV1(userId)

    const {
        runWorkspacesByUserQueryV1,
        modelWorkspacesByUserQueryV1: userWorkspacesModel
    } = useWorkspacesByUserQueryV1(userId)

    const {
        runWorkspacesByOrganizationQueryV1,
        modelWorkspacesByOrganizationQueryV1: organizationWorkspacesModel
    } = useWorkspacesByOrganizationQueryV1()

    // ---------------- derived ----------------

    const email = computed(() => user.value?.Email)
    const isServiceUser = computed(() => user.value?.IsServiceUser ?? false)
    const organizationUnitId = computed(() => user.value?.OrganizationUnitId)

    const userWorkspaces = computed(() => userWorkspacesModel.value?.Workspaces ?? [])
    const organizationWorkspaces = computed(() => organizationWorkspacesModel.value?.Workspaces ?? [])
    const organizationWorkspacesLoaded = computed(() => organizationWorkspacesModel.value != null)
    const organizationWorkspacesLength = computed(() => organizationWorkspaces.value.length)

    // combine org + user workspaces
    const workspaces = computed(() =>
        organizationWorkspaces.value.map((w: any) => ({
            ...w,
            Exists: userWorkspaces.value.some(
                (uw: any) => uw.WorkspaceId === w.WorkspaceId
            )
        }))
    )

    const userInWorkspaces = computed(() => workspaces.value.filter((w: any) => w.Exists))

    const userNotInWorkspaces = computed(() => workspaces.value.filter((w: any) => !w.Exists))

    // ---------------- commands ----------------

    const { invokeAddUserToWorkspaceCommandV1, succeedAddUserToWorkspaceCommandV1 } =
        useAddUserToWorkspaceCommandV1()

    const { invokeRemoveUserFromWorkspaceCommandV1, succeedRemoveUserFromWorkspaceCommandV1 } =
        useRemoveUserFromWorkspaceCommandV1()

    // ---------------- lifecycle ----------------

    onMounted(() =>
    {
        document.title = "Manage user's workspaces"
        runCurrentUserQueryV1()
        runUserQueryV1()
        runWorkspacesByUserQueryV1()
    })

    // ---------------- watchers ----------------

    watch(organizationUnitId, (ouId) =>
    {
        if (ouId)
        {
            runWorkspacesByOrganizationQueryV1({ tenantId: ouId })
        }
    })

    // Vue 2 succeedAnyCommand → refresh user workspaces
    watch(
        [
            succeedAddUserToWorkspaceCommandV1,
            succeedRemoveUserFromWorkspaceCommandV1
        ],
        (states) =>
        {
            if (states.some(Boolean))
            {
                runWorkspacesByUserQueryV1()
            }
        }
    )

    // ---------------- handlers ----------------

    function handleAddUserToWorkspaceCommandV1(workspace: any)
    {
        invokeAddUserToWorkspaceCommandV1({ id: workspace.WorkspaceId, userId: userId })
    }

    function handleRemoveUserFromWorkspaceCommandV1(workspace: any)
    {
        const type = isServiceUser.value ? 'service account' : 'user'
        if (window.confirm(`Do you want to remove ${type} ${email.value} from workspace?`))
        {
            invokeRemoveUserFromWorkspaceCommandV1({ id: workspace.WorkspaceId, userId: userId })
        }
    }
</script>
